import { useRef, useCallback, useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { useFirebase } from '../contexts/FirebaseContext';

// Static audio paths for pre-generated announcements (fallbacks only)
const STATIC_AUDIO_BASE = '/audio/announcements';

// Utility to get a random item from an array
const getRandomItem = <T>(items: T[]): T | undefined => {
  if (!items || items.length === 0) return undefined;
  const index = Math.floor(Math.random() * items.length);
  return items[index];
};

/**
 * Hook for playing ElevenLabs TTS audio
 * Handles playback of pre-generated audio and on-demand generation for "that's wrong"
 */
// Silent audio for unlocking audio context
const SILENT_AUDIO = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIGWi77+efTRAMUKfj8LZjHAY4kdfyzHksBSR3x/DdkEAKFF606euoVRQKRp/g8r5sIQUrgc7y2Yk2CBlou+/nn00QDFCn4/C2YxwGOJHX8sx5LAUkd8fw3ZBACg==';

export const useElevenLabsTTS = () => {
  const { functions } = useFirebase();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAudioUnlocked, setIsAudioUnlocked] = useState(false);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const wrongAnswerAudioRef = useRef<string | null>(null);
  const audioContextUnlockedRef = useRef(false);

  // Function to unlock audio context - can be called programmatically
  const unlockAudio = useCallback(async (): Promise<boolean> => {
    if (audioContextUnlockedRef.current) return true;

    // Create a dummy audio element and play/pause it to unlock audio context
    const dummyAudio = new Audio();
    dummyAudio.src = SILENT_AUDIO;
    dummyAudio.volume = 0.01;

    try {
      await dummyAudio.play();
      dummyAudio.pause();
      audioContextUnlockedRef.current = true;
      setIsAudioUnlocked(true);
      console.log('[TTS] Audio context unlocked');
      return true;
    } catch (e) {
      console.log('[TTS] Failed to unlock audio context:', e);
      return false;
    }
  }, []);

  // Try to unlock audio context on first user interaction
  useEffect(() => {
    // Try to unlock on any user interaction
    const events = ['click', 'touchstart', 'keydown'];
    const handlers = events.map(() => {
      const handler = () => {
        unlockAudio();
        events.forEach((e, idx) => document.removeEventListener(e, handlers[idx]));
      };
      return handler;
    });

    events.forEach((event, i) => {
      document.addEventListener(event, handlers[i], { once: true });
    });

    return () => {
      events.forEach((event, i) => {
        document.removeEventListener(event, handlers[i]);
      });
    };
  }, [unlockAudio]);

  // Generate "that's wrong" audio on-demand (cached after first generation)
  const generateWrongAnswerAudio = useCallback(async (): Promise<string | null> => {
    // If we already have it cached, return it
    if (wrongAnswerAudioRef.current) {
      return wrongAnswerAudioRef.current;
    }

    try {
      const generateTTS = httpsCallable<{ text: string }, { audioUrl: string }>(
        functions,
        'generateTTS'
      );
      const result = await generateTTS({ text: "That's wrong." });
      const audioUrl = result.data.audioUrl;
      wrongAnswerAudioRef.current = audioUrl;
      return audioUrl;
    } catch (error) {
      console.error('Error generating "that\'s wrong" audio:', error);
      return null;
    }
  }, [functions]);

  /**
   * Play audio from a base64 data URL
   * Returns a Promise that resolves when playback completes
   */
  const playAudio = useCallback((audioUrl: string | null | undefined): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!audioUrl) {
        // No audio available, resolve immediately
        resolve();
        return;
      }

      // Stop any currently playing audio
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }

      try {
        const audio = new Audio(audioUrl);
        audio.volume = 0.8;
        currentAudioRef.current = audio;
        setIsPlaying(true);

        const handleEnded = () => {
          setIsPlaying(false);
          currentAudioRef.current = null;
          resolve();
        };

        const handleError = (error: ErrorEvent) => {
          console.error('Error playing audio:', error);
          setIsPlaying(false);
          currentAudioRef.current = null;
          // Resolve anyway to not block the game flow
          resolve();
        };

        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('error', handleError);

        // Preload and play audio - handle autoplay policy
        // First, try to play immediately (works if user interaction just happened)
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            console.error('Error starting audio playback (autoplay blocked):', error);
            // If autoplay is blocked, we can't play automatically
            // The user will need to interact with the page first
            // For now, resolve to not block the game flow
            setIsPlaying(false);
            currentAudioRef.current = null;
            resolve();
          });
        }
      } catch (error) {
        console.error('Error creating audio element:', error);
        setIsPlaying(false);
        resolve();
      }
    });
  }, []);

  /**
   * Play question audio for a clue
   */
  const playQuestionAudio = useCallback(
    (audioUrl: string | null | undefined): Promise<void> => {
      return playAudio(audioUrl);
    },
    [playAudio]
  );

  /**
   * Play answer audio for a clue
   */
  const playAnswerAudio = useCallback(
    (audioUrl: string | null | undefined): Promise<void> => {
      return playAudio(audioUrl);
    },
    [playAudio]
  );

  /**
   * Play "that's wrong" audio
   * Generates on-demand if not already cached
   */
  const playWrongAnswer = useCallback(async (): Promise<void> => {
    const audioUrl = await generateWrongAnswerAudio();
    return playAudio(audioUrl);
  }, [generateWrongAnswerAudio, playAudio]);

  /**
   * Play personalized "correct" feedback - picks random variation from array
   */
  const playPersonalizedCorrect = useCallback(
    (personalizedUrls?: string[]): Promise<void> => {
      const audioUrl = getRandomItem(personalizedUrls || []);
      if (audioUrl) {
        return playAudio(audioUrl);
      }
      // Fallback to static clip if no personalized audio
      return playAudio(`${STATIC_AUDIO_BASE}/correct/01.mp3`);
    },
    [playAudio]
  );

  /**
   * Play personalized "incorrect" feedback - picks random variation from array
   */
  const playPersonalizedIncorrect = useCallback(
    (personalizedUrls?: string[]): Promise<void> => {
      const audioUrl = getRandomItem(personalizedUrls || []);
      if (audioUrl) {
        return playAudio(audioUrl);
      }
      // Fallback to static clip if no personalized audio
      return playAudio(`${STATIC_AUDIO_BASE}/incorrect/01.mp3`);
    },
    [playAudio]
  );

  /**
   * Play board control announcement - picks random variation from array
   * Only plays if personalized audio exists (no fallback to partial phrases)
   */
  const playBoardControl = useCallback(
    (personalizedUrls?: string[]): Promise<void> => {
      const audioUrl = getRandomItem(personalizedUrls || []);
      if (audioUrl) {
        return playAudio(audioUrl);
      }
      // No fallback - partial phrases like "controls the board" don't make sense without a name
      return Promise.resolve();
    },
    [playAudio]
  );

  /**
   * Play category announcement audio
   */
  const playCategoryAnnouncement = useCallback(
    (categoryAudioUrl?: string): Promise<void> => {
      if (!categoryAudioUrl) {
        return Promise.resolve();
      }
      return playAudio(categoryAudioUrl);
    },
    [playAudio]
  );

  /**
   * Play answer reveal - the full phrase is pre-generated (e.g. "The correct answer was X")
   */
  const playAnswerReveal = useCallback(
    (answerAudioUrl?: string): Promise<void> => {
      if (answerAudioUrl) {
        return playAudio(answerAudioUrl);
      }
      return Promise.resolve();
    },
    [playAudio]
  );

  /**
   * Play "Time's up! Nobody got it." announcement
   */
  const playNobodyGotIt = useCallback((): Promise<void> => {
    const audioPath = `${STATIC_AUDIO_BASE}/reveal/nobody-got.mp3`;
    return playAudio(audioPath);
  }, [playAudio]);

  /**
   * Play "Time's up!" announcement
   */
  const playTimesUp = useCallback((): Promise<void> => {
    const audioPath = `${STATIC_AUDIO_BASE}/reveal/times-up.mp3`;
    return playAudio(audioPath);
  }, [playAudio]);

  /**
   * Stop any currently playing audio
   */
  const stopAudio = useCallback(() => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
      setIsPlaying(false);
    }
  }, []);

  return {
    playQuestionAudio,
    playAnswerAudio,
    playWrongAnswer,
    playPersonalizedCorrect,
    playPersonalizedIncorrect,
    playBoardControl,
    playCategoryAnnouncement,
    playAnswerReveal,
    playNobodyGotIt,
    playTimesUp,
    stopAudio,
    unlockAudio,
    isPlaying,
    isAudioUnlocked,
  };
};

